import configuration from '../configuration.json';
import { locale } from 'svelte-i18n';
import { get } from 'svelte/store';
import * as Sentry from './telemetry.js';

export class ApiError extends Error {
    constructor(code) {
        super(code);
        this.code = code;
    }
}

const RETRY_DELAYS = [1000, 2000, 4000];
const FETCH_TIMEOUT = 10000;
const NON_RETRYABLE_ERRORS = ['EMPTY_RESPONSE', 'HTTP_400', 'HTTP_401', 'HTTP_403', 'HTTP_404', 'HTTP_405', 'HTTP_422'];

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function createTimeout(ms) {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), ms);
    return controller.signal;
}

async function checkInternetConnection() {
    try {
        await fetch('https://www.google.com/generate_204', {
            mode: 'no-cors',
            signal: createTimeout(5000)
        });
        return true;
    } catch {
        return false;
    }
}

let connectionErrorDialog;
let navigationGeneration = 0;

export function registerConnectionErrorDialog(dialog) {
    connectionErrorDialog = dialog;
}

/**
 * Call when the user navigates away to cancel any in-flight retries.
 */
export function cancelPendingRetries() {
    navigationGeneration++;
}

function throwIfCancelled(generation) {
    if (generation !== navigationGeneration) {
        throw new DOMException('Navigation cancelled', 'AbortError');
    }
}

/**
 * Fetch data from the SalonHub API with automatic retry on failure.
 * Shows a connection error dialog when all retries are exhausted.
 *
 * @param {Object} options
 * @param {string} options.endpoint - API endpoint name (e.g. 'OnlineAppointment.Remote.Dates/get')
 * @param {FormData} [options.data] - Form data to send (POST)
 * @param {Object} [options.params] - Query parameters (GET)
 * @param {number} [options.retries=3] - Number of retries (0 to disable)
 * @param {string} [options.language] - Accept-Language for this request (defaults to the widget locale)
 * @returns {Promise<any>} Parsed JSON response
 */
export async function fetchApi({ endpoint, data, params, retries = 3, language }) {
    const generation = navigationGeneration;

    let url = `${configuration.endpoint}/${endpoint}`;

    if (params) {
        url += '?' + new URLSearchParams(params).toString();
    }

    let errorCode = 'NO_CONNECTION';
    let lastError = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
        if (attempt > 0) {
            throwIfCancelled(generation);
            await delay(RETRY_DELAYS[attempt - 1]);
        }

        let res;

        try {
            res = await fetch(url, {
                method: data ? 'POST' : 'GET',
                body: data || undefined,
                signal: createTimeout(FETCH_TIMEOUT),
                headers: {
                    'Authorization': `Bearer ${configuration.apikey}`,
                    // Explicit per-request language (agent tool calls pass theirs);
                    // otherwise the widget locale.
                    'Accept-Language': language || get(locale)
                }
            });
        } catch (error) {
            errorCode = 'NO_CONNECTION';
            lastError = { type: 'exception', error, attempt: attempt + 1 };
            continue;
        }

        if (!res.ok) {
            errorCode = `HTTP_${res.status}`;
            lastError = { type: 'http', status: res.status, attempt: attempt + 1 };

            if (NON_RETRYABLE_ERRORS.includes(errorCode)) break;
            continue;
        }

        const text = await res.text();

        if (!text) {
            errorCode = 'EMPTY_RESPONSE';
            lastError = { type: 'empty', attempt: attempt + 1 };
            continue;
        }

        let json;

        try {
            json = JSON.parse(text);
        } catch (error) {
            errorCode = 'JSON_INVALID';
            lastError = { type: 'json', error, text, attempt: attempt + 1 };
            continue;
        }

        return json;
    }

    // All retries exhausted — check connectivity
    const hasInternet = await checkInternetConnection();

    if (errorCode === 'NO_CONNECTION' && hasInternet) {
        errorCode = 'SERVER_UNREACHABLE';
    }

    // Report to Sentry
    Sentry.captureException(lastError?.error || new Error(errorCode), {
        fingerprint: ['fetchApi', endpoint, errorCode],
        tags: {
            errorCode,
            endpoint
        },
        extra: {
            url,
            params: data ? Object.fromEntries(data) : params,
            attempts: lastError?.attempt,
            status: lastError?.status,
            responseText: lastError?.text,
            hasInternet
        }
    });

    // Don't show dialog or retry if the user navigated away
    throwIfCancelled(generation);

    // Non-retryable errors should be handled by the caller, not the connection dialog
    if (NON_RETRYABLE_ERRORS.includes(errorCode)) {
        throw new ApiError(errorCode);
    }

    // Show dialog and retry on user action
    if (connectionErrorDialog) {
        await connectionErrorDialog.show(errorCode);

        if (generation !== navigationGeneration) return;

        return fetchApi({ endpoint, data, retries, language });
    }

    throw new ApiError(errorCode);
}
